import React, { useState, useCallback, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { debounce } from 'lodash';
import { FaMusic, FaTimes } from 'react-icons/fa';

export default function TrackSearchSelector({ selectedTrack, onSelectTrack, isDarkMode = true }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const functions = getFunctions();
  const searchAppContent = httpsCallable(functions, 'searchAppContent');

  // Perform search (debounced)
  const performSearch = useCallback(
    debounce(async (term) => {
      if (term.length < 2) {
        setResults([]);
        return;
      }
      setIsLoading(true);
      try {
        const response = await searchAppContent({ searchTerm: term });
        const allResults = response.data?.results || [];
        // Filter search results exclusively for songs for the copyright form
        const songResults = allResults.filter((item) => item.type === 'song');
        setResults(songResults);
      } catch (error) {
        console.error('Track search failed:', error);
      } finally {
        setIsLoading(false);
      }
    }, 400),
    []
  );

  useEffect(() => {
    if (!selectedTrack) {
      performSearch(searchTerm);
    }
  }, [searchTerm, performSearch, selectedTrack]);

  // Handle selecting a track
  const handleSelect = (song) => {
    onSelectTrack(song);
    setSearchTerm('');
    setResults([]);
  };

  // Handle clearing selection
  const handleClearSelection = () => {
    onSelectTrack(null);
  };

  // If a track is already selected, display a selected badge
  if (selectedTrack) {
    return (
      <div className={`flex items-center justify-between p-3 rounded-lg border ${
        isDarkMode ? 'bg-zinc-900 border-zinc-700 text-white' : 'bg-gray-100 border-gray-300 text-gray-900'
      }`}>
        <div className="flex items-center space-x-3 overflow-hidden">
          {selectedTrack.imageUrl ? (
            <img 
              src={selectedTrack.imageUrl} 
              alt={selectedTrack.title} 
              className="w-12 h-12 object-cover rounded"
            />
          ) : (
            <div className="w-12 h-12 bg-zinc-800 rounded flex items-center justify-center">
              <FaMusic className="text-zinc-500" />
            </div>
          )}
          <div className="truncate">
            <p className="font-semibold text-sm truncate">{selectedTrack.title}</p>
            <p className="text-xs text-zinc-400 truncate">{selectedTrack.subtitle || 'Artist'}</p>
            <p className="text-[10px] text-zinc-500 font-mono">ID: {selectedTrack.id}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleClearSelection}
          className="p-2 text-zinc-400 hover:text-red-500 transition-colors"
          title="Remove selected track"
        >
          <FaTimes />
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <input
        type="text"
        placeholder="Type to search for a track or artist on Atomic..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className={`w-full p-3 border rounded-lg transition-colors duration-200 outline-none ${
          isDarkMode
            ? 'bg-zinc-900 border-zinc-800 text-white placeholder-zinc-500 focus:border-red-500'
            : 'bg-white border-slate-200 text-slate-800 placeholder-slate-400 focus:border-red-500'
        }`}
      />

      {/* Loading state indicator */}
      {isLoading && (
        <p className="text-xs text-zinc-400 mt-2 px-1">Searching catalog...</p>
      )}

      {/* Search Dropdown Results */}
      {results.length > 0 && !selectedTrack && (
        <div className={`absolute left-0 right-0 mt-2 max-h-60 overflow-y-auto z-50 rounded-lg border shadow-xl ${
          isDarkMode ? 'bg-zinc-900 border-zinc-700 text-white' : 'bg-white border-gray-200 text-gray-900'
        }`}>
          {results.map((song) => (
            <div
              key={song.id}
              onClick={() => handleSelect(song)}
              className={`flex items-center p-3 cursor-pointer border-b last:border-b-0 transition-colors ${
                isDarkMode 
                  ? 'border-zinc-800 hover:bg-zinc-800' 
                  : 'border-gray-100 hover:bg-gray-50'
              }`}
            >
              {song.imageUrl ? (
                <img src={song.imageUrl} alt={song.title} className="w-10 h-10 object-cover rounded mr-3" />
              ) : (
                <div className="w-10 h-10 bg-zinc-800 rounded flex items-center justify-center mr-3">
                  <FaMusic className="text-zinc-500" />
                </div>
              )}
              <div className="flex-grow min-w-0">
                <p className="font-semibold text-sm truncate">{song.title}</p>
                <p className="text-xs text-zinc-400 truncate">{song.subtitle}</p>
              </div>
              <span className="text-xs font-bold text-red-500 ml-2">Select</span>
            </div>
          ))}
        </div>
      )}

      {searchTerm.length >= 2 && !isLoading && results.length === 0 && !selectedTrack && (
        <p className="text-xs text-zinc-500 mt-2 px-1">No matching tracks found on Atomic.</p>
      )}
    </div>
  );
}